import React from "react";
import styled from "styled-components";
import TopNavbar from "../../publicTemplate/components/Nav/TopNavbar";
import Footer from "../../publicTemplate/components/Sections/Footer";
import FullButton from "../../publicTemplate/components/Buttons/FullButton";
import "../../publicTemplate/style/index.css";

const tips = [
  {
    number: 1,
    title: "Focus on a single question.",
    summary:
      "Where it is easy to distinguish separate issues, ask about those issues in separate questions dedicated to each issue, rather than combining them.",
    detail: "",
  },
  {
    number: 2,
    title: "Write a clear, concise, and focused query that is 1 to 2 sentences long.",
    summary: "Avoid irrelevant facts.",
    detail:
      "Include information such as country, relevant primary law, desired outcome, and material facts to provide better context for the query.",
  },
  {
    number: 3,
    title: "Write your query as a question, the same way you might ask a colleague in an email.",
    summary: "Use natural language for best results.",
    detail:
      "Do not write a query in the style of a prompt, command, or instruction. We have done the prompting and instruction work for you and have designed Click Companion to work best with legal research queries that clearly identify the information you would like to find. Think of Click Companion as your research assistant.",
  },
  {
    number: 4,
    title: "Use Click Companion to accelerate thorough research, not to replace it.",
    summary: "Click Companion does hours of work in minutes, but you need to review it.",
    detail:
      "Never use it to advise a client, draft a brief or otherwise rely on it without further validation.",
  },
];

export default function TipsPage() {
  return (
    <>
      <TopNavbar />

      {/* Hero */}
      <HeroWrapper id="tips-hero" className="darkBg">
        <div className="container">
          <h1 className="font40 extraBold whiteColor">Click Companion Tips</h1>
          <HeroSub className="font15 whiteColor">
            Get the most out of Click Companion with these best practices for legal research queries.
          </HeroSub>
        </div>
      </HeroWrapper>

      {/* Tips section */}
      <TipsWrapper className="whiteBg">
        <div className="container">
          <HeaderInfo>
            <h2 className="font30 extraBold">How to use Click Companion effectively</h2>
            <p className="font14 greyColor" style={{ marginTop: "10px" }}>
              Follow these guidelines to get the best results from your AI legal research assistant.
            </p>
          </HeaderInfo>

          <Separator />

          <TipsGrid>
            {tips.map((tip) => (
              <TipCard key={tip.number} className="whiteBg">
                <Badge>
                  <span className="font15 extraBold whiteColor">{tip.number}</span>
                </Badge>
                <h3 className="font18 semiBold" style={{ marginTop: "16px", marginBottom: "8px" }}>
                  {tip.title}
                </h3>
                <p className="font14">{tip.summary}</p>
                {tip.detail && (
                  <p className="font14 greyColor" style={{ marginTop: "8px" }}>
                    {tip.detail}
                  </p>
                )}
              </TipCard>
            ))}
          </TipsGrid>
        </div>
      </TipsWrapper>

      {/* CTA */}
      <CtaWrapper className="lightBg">
        <div className="container textCenter">
          <h2 className="font30 extraBold">Ready to get started?</h2>
          <CtaSub className="font15 greyColor">
            Sign in to start using Click Companion for your legal research.
          </CtaSub>
          <BtnRow>
            <BtnInner>
              <FullButton title="Sign In" action={() => { window.location.href = "/sign-in"; }} border={false} />
            </BtnInner>
            <BtnInner>
              <FullButton title="Back to Home" border action={() => { window.location.href = "/"; }} />
            </BtnInner>
          </BtnRow>
        </div>
      </CtaWrapper>

      <Footer />
    </>
  );
}

/* ── Styled components ── */

const HeroWrapper = styled.section`
  width: 100%;
  padding: 140px 0 80px;
  @media (max-width: 860px) {
    padding: 110px 0 60px;
  }
`;

const HeroSub = styled.p`
  max-width: 600px;
  margin-top: 16px;
  opacity: 0.8;
  line-height: 1.6;
`;

const TipsWrapper = styled.section`
  width: 100%;
  padding: 70px 0 80px;
`;

const HeaderInfo = styled.div`
  max-width: 600px;
  @media (max-width: 860px) {
    text-align: center;
    margin: 0 auto;
  }
`;

const Separator = styled.hr`
  border: none;
  border-top: 5px solid #d4af37;
  margin: 30px 0 50px;
`;

const TipsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const TipCard = styled.div`
  border: 1px solid rgba(6, 57, 34, 0.12);
  border-radius: 1rem;
  padding: 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.3s ease;
  :hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
`;

const Badge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #d4af37;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CtaWrapper = styled.section`
  width: 100%;
  padding: 80px 0;
`;

const CtaSub = styled.p`
  margin: 16px auto 36px;
  max-width: 480px;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const BtnInner = styled.div`
  width: 180px;
`;
